import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../../store';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';

/**
 * Unified admin top bar: brand · grouped responsive nav · (email + sign out) far-right.
 * Pass `active` = the current section key so the matching item is highlighted.
 * Reads the signed-in user + logout from the auth store internally so pages only
 * need to render <AdminHeader active="leads" />.
 */

// Flat top-level links (always visible)
const TOP_LINKS = [
  { key: 'leads', label: 'Leads', to: '/admin/leads' },
  { key: 'analytics', label: 'Analytics', to: '/admin/analytics' },
];

// Grouped links collapsed into dropdowns to free horizontal space
const GROUPS = [
  {
    label: 'Catalog',
    items: [
      { key: 'collections', label: 'Collections', to: '/admin/collections' },
      { key: 'products', label: 'Products', to: '/admin/products' },
      { key: 'projects', label: 'Projects', to: '/admin/projects' },
      { key: 'journal', label: 'Journal', to: '/admin/journal' },
    ],
  },
  {
    label: 'Content',
    items: [
      { key: 'about', label: 'About Us', to: '/admin/about' },
      { key: 'contacts', label: 'Contacts', to: '/admin/contacts' },
    ],
  },
  {
    label: 'System',
    items: [
      { key: 'appearance', label: 'Appearance', to: '/admin/appearance' },
      { key: 'content', label: 'Site Copy', to: '/admin/content' },
      { key: 'settings', label: 'Settings', to: '/admin/settings' },
    ],
  },
];

const AdminHeader = ({ active }) => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const linkClass = (isActive) =>
    `whitespace-nowrap transition-colors ${isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`;

  return (
    <header className="border-b border-border bg-background sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        {/* Left: brand + nav */}
        <div className="flex items-center gap-8 min-w-0">
          <Link to="/en" className="font-heading text-lg font-light tracking-tight whitespace-nowrap flex-shrink-0">
            FEDYK<span className="font-extralight"> Furniture</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm" data-testid="admin-nav">
            {TOP_LINKS.map((link) =>
              link.key === active ? (
                <span key={link.key} className={linkClass(true)} data-testid={`admin-nav-${link.key}`}>
                  {link.label}
                </span>
              ) : (
                <Link key={link.key} to={link.to} className={linkClass(false)} data-testid={`admin-nav-${link.key}`}>
                  {link.label}
                </Link>
              ),
            )}

            {GROUPS.map((group) => {
              const groupActive = group.items.some((i) => i.key === active);
              return (
                <DropdownMenu key={group.label}>
                  <DropdownMenuTrigger
                    className={`flex items-center gap-1 outline-none ${linkClass(groupActive)}`}
                    data-testid={`admin-nav-group-${group.label.toLowerCase()}`}
                  >
                    {group.label}
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-44">
                    {group.items.map((item) => (
                      <DropdownMenuItem
                        key={item.key}
                        onClick={() => navigate(item.to)}
                        className={`cursor-pointer ${item.key === active ? 'text-foreground font-medium' : ''}`}
                        data-testid={`admin-nav-${item.key}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </nav>
        </div>

        {/* Right: email + sign out */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className="text-xs text-muted-foreground hidden lg:inline max-w-[200px] truncate" data-testid="admin-user-email">
            {user?.email}
          </span>
          <button
            onClick={() => { logout(); navigate('/admin'); }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </div>

      {/* Mobile nav: horizontal scroll row below the bar */}
      <nav className="md:hidden flex items-center gap-4 text-sm px-6 pb-3 overflow-x-auto" data-testid="admin-nav-mobile">
        {[...TOP_LINKS, ...GROUPS.flatMap((g) => g.items)].map((link) =>
          link.key === active ? (
            <span key={link.key} className="text-foreground whitespace-nowrap">{link.label}</span>
          ) : (
            <Link key={link.key} to={link.to} className="text-muted-foreground hover:text-foreground whitespace-nowrap transition-colors">
              {link.label}
            </Link>
          ),
        )}
      </nav>
    </header>
  );
};

export default AdminHeader;
